import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { ClipboardList, CheckCircle, Clock, Users, ArrowRight, Send, Gift, Trophy, Target, Loader2, Camera, MapPin, Sparkles, Heart, Recycle, Trash2, BookOpen, Upload } from 'lucide-react';
import { cn } from '../../lib/utils';
interface Survey {
  id: string;
  title: string;
  description: string;
  questions: number;
  duration: string;
  participants: number;
  deadline: string;
  status: 'active' | 'closed' | 'upcoming';
  reward?: string;
  category: string;
}
const mockSurveys: Survey[] = [{
  id: '1',
  title: 'Survei Kepuasan Layanan Pengelolaan Sampah',
  description: 'Bantu kami meningkatkan layanan pengelolaan sampah di wilayah Anda dengan mengisi survei singkat ini.',
  questions: 10,
  duration: '5 menit',
  participants: 1250,
  deadline: '28 Feb 2024',
  status: 'active',
  reward: 'E-Certificate',
  category: 'Layanan Publik'
}, {
  id: '2',
  title: 'Pola Pemilahan Sampah Rumah Tangga',
  description: 'Survei untuk memahami kebiasaan pemilahan sampah di tingkat rumah tangga.',
  questions: 15,
  duration: '8 menit',
  participants: 890,
  deadline: '15 Mar 2024',
  status: 'active',
  category: 'Riset'
}, {
  id: '3',
  title: 'Evaluasi Program HPSN 2024',
  description: 'Berikan penilaian Anda terhadap pelaksanaan Hari Peduli Sampah Nasional 2024.',
  questions: 8,
  duration: '4 menit',
  participants: 2100,
  deadline: '05 Mar 2024',
  status: 'active',
  reward: 'Poin Reward',
  category: 'Evaluasi'
}, {
  id: '4',
  title: 'Preferensi Konten Edukasi',
  description: 'Bantu kami memahami jenis konten edukasi yang paling Anda butuhkan.',
  questions: 12,
  duration: '6 menit',
  participants: 560,
  deadline: '20 Mar 2024',
  status: 'upcoming',
  category: 'Riset'
}];
const actionCategories = [{
  id: 'cleanup',
  label: 'Aksi Bersih-Bersih',
  icon: Trash2,
  color: 'bg-blue-100 text-blue-600'
}, {
  id: 'pilah',
  label: 'Pemilahan Sampah',
  icon: Recycle,
  color: 'bg-green-100 text-green-600'
}, {
  id: 'edukasi',
  label: 'Edukasi Lingkungan',
  icon: BookOpen,
  color: 'bg-purple-100 text-purple-600'
}, {
  id: 'other',
  label: 'Aksi Lainnya',
  icon: Heart,
  color: 'bg-pink-100 text-pink-600'
}];
export function PartisipasiSurveiPage() {
  const [activeTab, setActiveTab] = useState<'survei' | 'aksi'>('survei');
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [answers, setAnswers] = useState<Record<number, string | number>>({});
  // Aksi Mandiri state
  const [aksiForm, setAksiForm] = useState({
    category: '',
    description: '',
    location: '',
    photo: null as File | null
  });
  const [aksiSubmitted, setAksiSubmitted] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  // Mock questions for the survey
  const mockQuestions = [{
    id: 1,
    type: 'rating',
    question: 'Seberapa puas Anda dengan layanan pengangkutan sampah di wilayah Anda?',
    options: [1, 2, 3, 4, 5]
  }, {
    id: 2,
    type: 'multiple',
    question: 'Jenis sampah apa yang paling banyak Anda hasilkan?',
    options: ['Organik (sisa makanan)', 'Plastik', 'Kertas', 'Kaca/Logam', 'Lainnya']
  }, {
    id: 3,
    type: 'multiple',
    question: 'Apakah Anda sudah melakukan pemilahan sampah di rumah?',
    options: ['Ya, selalu', 'Kadang-kadang', 'Jarang', 'Belum pernah']
  }, {
    id: 4,
    type: 'text',
    question: 'Apa kendala utama Anda dalam mengelola sampah rumah tangga?'
  }, {
    id: 5,
    type: 'rating',
    question: 'Seberapa sering Anda mengikuti kegiatan bersih-bersih lingkungan?',
    options: [1, 2, 3, 4, 5],
    labels: ['Tidak Pernah', 'Jarang', 'Kadang', 'Sering', 'Selalu']
  }];
  const handleAnswer = (questionId: number, answer: string | number) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };
  const handleNext = () => {
    if (currentQuestion < mockQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmitSurvey();
    }
  };
  const handleSubmitSurvey = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsCompleted(true);
    }, 1500);
  };
  const resetSurvey = () => {
    setSelectedSurvey(null);
    setCurrentQuestion(0);
    setAnswers({});
    setIsCompleted(false);
  };
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAksiForm({
        ...aksiForm,
        photo: file
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSubmitAksi = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setAksiSubmitted(true);
    }, 1500);
  };
  const resetAksi = () => {
    setAksiForm({
      category: '',
      description: '',
      location: '',
      photo: null
    });
    setPhotoPreview(null);
    setAksiSubmitted(false);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700 border-green-200" data-id="element-4737">
            Aktif
          </Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200" data-id="element-4738">
            Ditutup
          </Badge>;
      case 'upcoming':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200" data-id="element-4739">
            Segera
          </Badge>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-4740">
      <Header data-id="element-4741" />

      <main className="flex-1" data-id="element-4742">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-600 to-secondary-700 text-white py-16" data-id="element-4743">
          <div className="container mx-auto px-4 md:px-6 text-center" data-id="element-4744">
            <Badge className="mb-4 bg-white/20 text-white border-white/30" data-id="element-4745">
              Partisipasi Publik
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-4746">
              Suarakan Pendapat & Aksi Anda
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto" data-id="element-4747">
              Ikuti survei, berikan masukan, atau laporkan aksi mandiri Anda
              untuk Indonesia yang lebih bersih.
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-8 container mx-auto px-4 md:px-6 -mt-8 relative z-20" data-id="element-4748">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4749">
            <Card className="bg-white shadow-lg border-0" data-id="element-4750">
              <CardContent className="p-4 flex items-center gap-4" data-id="element-4751">
                <div className="p-3 bg-primary-100 rounded-full text-primary-600" data-id="element-4752">
                  <ClipboardList className="h-6 w-6" data-id="element-4753" />
                </div>
                <div data-id="element-4754">
                  <p className="text-sm text-gray-500" data-id="element-4755">Survei Aktif</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-4756">12</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg border-0" data-id="element-4757">
              <CardContent className="p-4 flex items-center gap-4" data-id="element-4758">
                <div className="p-3 bg-green-100 rounded-full text-green-600" data-id="element-4759">
                  <Users className="h-6 w-6" data-id="element-4760" />
                </div>
                <div data-id="element-4761">
                  <p className="text-sm text-gray-500" data-id="element-4762">Total Partisipan</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-4763">24.5K</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg border-0" data-id="element-4764">
              <CardContent className="p-4 flex items-center gap-4" data-id="element-4765">
                <div className="p-3 bg-blue-100 rounded-full text-blue-600" data-id="element-4766">
                  <Sparkles className="h-6 w-6" data-id="element-4767" />
                </div>
                <div data-id="element-4768">
                  <p className="text-sm text-gray-500" data-id="element-4769">Aksi Dilaporkan</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-4770">8.2K</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-lg border-0" data-id="element-4771">
              <CardContent className="p-4 flex items-center gap-4" data-id="element-4772">
                <div className="p-3 bg-orange-100 rounded-full text-orange-600" data-id="element-4773">
                  <Trophy className="h-6 w-6" data-id="element-4774" />
                </div>
                <div data-id="element-4775">
                  <p className="text-sm text-gray-500" data-id="element-4776">Reward Tersedia</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-4777">5</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="container mx-auto px-4 md:px-6 pt-8" data-id="element-4778">
          <div className="flex bg-white rounded-xl p-1.5 border shadow-sm max-w-md mx-auto" data-id="element-4779">
            <button onClick={() => setActiveTab('survei')} className={cn('flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all', activeTab === 'survei' ? 'bg-primary-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50')} data-id="element-4780">
              <ClipboardList className="h-4 w-4" data-id="element-4781" />
              Isi Survei
            </button>
            <button onClick={() => setActiveTab('aksi')} className={cn('flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all', activeTab === 'aksi' ? 'bg-primary-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50')} data-id="element-4782">
              <Sparkles className="h-4 w-4" data-id="element-4783" />
              Laporkan Aksi
            </button>
          </div>
        </section>

        {/* Content based on Tab */}
        {activeTab === 'survei' ? <section className="py-12 container mx-auto px-4 md:px-6" data-id="element-4784">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4785">
              Survei Tersedia
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4786">
              {mockSurveys.map(survey => <Card key={survey.id} className="hover:shadow-lg transition-shadow" data-id="element-4787">
                  <CardContent className="p-6" data-id="element-4788">
                    <div className="flex items-start justify-between mb-3" data-id="element-4789">
                      <Badge variant="secondary" className="text-xs" data-id="element-4790">
                        {survey.category}
                      </Badge>
                      {getStatusBadge(survey.status)}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-4791">
                      {survey.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2" data-id="element-4792">
                      {survey.description}
                    </p>

                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4" data-id="element-4793">
                      <span className="flex items-center gap-1" data-id="element-4794">
                        <ClipboardList className="h-4 w-4" data-id="element-4795" />
                        {survey.questions} pertanyaan
                      </span>
                      <span className="flex items-center gap-1" data-id="element-4796">
                        <Clock className="h-4 w-4" data-id="element-4797" />
                        {survey.duration}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-4798">
                        <Users className="h-4 w-4" data-id="element-4799" />
                        {survey.participants.toLocaleString()}
                      </span>
                    </div>

                    {survey.reward && <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 px-3 py-1.5 rounded-full w-fit mb-4" data-id="element-4800">
                        <Gift className="h-4 w-4" data-id="element-4801" />
                        <span className="font-medium" data-id="element-4802">{survey.reward}</span>
                      </div>}

                    <div className="flex items-center justify-between pt-4 border-t" data-id="element-4803">
                      <span className="text-xs text-gray-500" data-id="element-4804">
                        Deadline: {survey.deadline}
                      </span>
                      <Button className="bg-primary-600 hover:bg-primary-700 gap-2" onClick={() => setSelectedSurvey(survey)} disabled={survey.status !== 'active'} data-id="element-4805">
                        {survey.status === 'active' ? 'Mulai Survei' : 'Segera Hadir'}
                        <ArrowRight className="h-4 w-4" data-id="element-4806" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </section> : <section className="py-12 container mx-auto px-4 md:px-6 max-w-2xl" data-id="element-4807">
            {aksiSubmitted ? <div className="text-center py-8 animate-in fade-in zoom-in duration-500" data-id="element-4808">
                <div className="h-24 w-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4809">
                  <CheckCircle className="h-12 w-12 text-green-600" data-id="element-4810" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4811">
                  Aksi Anda Tercatat!
                </h2>
                <p className="text-gray-600 mb-8 max-w-md mx-auto" data-id="element-4812">
                  Terima kasih telah berkontribusi untuk Indonesia yang lebih
                  bersih. Aksi Anda akan ditampilkan di dashboard partisipasi
                  nasional.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-4813">
                  <Button variant="outline" onClick={resetAksi} data-id="element-4814">
                    Laporkan Aksi Lain
                  </Button>
                  <Link to="/" data-id="element-4815">
                    <Button className="bg-primary-600 hover:bg-primary-700" data-id="element-4816">
                      Ke Beranda
                    </Button>
                  </Link>
                </div>
              </div> : <>
                <div className="text-center mb-8" data-id="element-4817">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4818">
                    Laporkan Aksi Mandiri
                  </h2>
                  <p className="text-gray-600" data-id="element-4819">
                    Sudah melakukan aksi lingkungan? Ceritakan dan
                    dokumentasikan aksi Anda!
                  </p>
                </div>

                <Card className="border-0 shadow-lg" data-id="element-4820">
                  <CardContent className="p-6 md:p-8" data-id="element-4821">
                    <form onSubmit={handleSubmitAksi} className="space-y-6" data-id="element-4822">
                      {/* Kategori Aksi */}
                      <div className="space-y-3" data-id="element-4823">
                        <label className="text-sm font-medium text-gray-900" data-id="element-4824">
                          Jenis Aksi <span className="text-red-500" data-id="element-4825">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3" data-id="element-4826">
                          {actionCategories.map(cat => <button key={cat.id} type="button" onClick={() => setAksiForm({
                      ...aksiForm,
                      category: cat.id
                    })} className={cn('flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-all', aksiForm.category === cat.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300')} data-id="element-4827">
                              <div className={cn('p-2 rounded-lg', cat.color)} data-id="element-4828">
                                <cat.icon className="h-5 w-5" data-id="element-4829" />
                              </div>
                              <span className="text-sm font-medium text-gray-900" data-id="element-4830">
                                {cat.label}
                              </span>
                            </button>)}
                        </div>
                      </div>

                      {/* Deskripsi */}
                      <div className="space-y-2" data-id="element-4831">
                        <label className="text-sm font-medium text-gray-900" data-id="element-4832">
                          Ceritakan Aksi Anda{' '}
                          <span className="text-red-500" data-id="element-4833">*</span>
                        </label>
                        <Textarea placeholder="Contoh: Hari ini saya dan keluarga membersihkan sampah di sekitar rumah dan memilah sampah organik untuk kompos..." className="min-h-[120px]" value={aksiForm.description} onChange={e => setAksiForm({
                    ...aksiForm,
                    description: e.target.value
                  })} required data-id="element-4834" />
                        <p className="text-xs text-gray-500 text-right" data-id="element-4835">
                          Minimal 20 karakter
                        </p>
                      </div>

                      {/* Foto (Opsional) */}
                      <div className="space-y-2" data-id="element-4836">
                        <label className="text-sm font-medium text-gray-900" data-id="element-4837">
                          Foto Dokumentasi (Opsional)
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 hover:border-primary-400 transition-colors relative cursor-pointer" data-id="element-4838">
                          <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" accept="image/*" onChange={handlePhotoUpload} data-id="element-4839" />
                          {photoPreview ? <div className="relative" data-id="element-4840">
                              <img src={photoPreview} alt="Preview" className="mx-auto h-40 object-cover rounded-md" data-id="element-4841" />
                              <p className="text-xs text-gray-500 mt-2" data-id="element-4842">
                                Klik untuk ganti foto
                              </p>
                            </div> : <>
                              <Camera className="mx-auto h-10 w-10 text-gray-400 mb-2" data-id="element-4843" />
                              <p className="text-sm text-gray-600" data-id="element-4844">
                                Klik untuk upload foto
                              </p>
                              <p className="text-xs text-gray-400 mt-1" data-id="element-4845">
                                PNG, JPG (Max. 5MB)
                              </p>
                            </>}
                        </div>
                      </div>

                      {/* Lokasi (Opsional) */}
                      <div className="space-y-2" data-id="element-4846">
                        <label className="text-sm font-medium text-gray-900" data-id="element-4847">
                          Lokasi (Opsional)
                        </label>
                        <div className="flex gap-2" data-id="element-4848">
                          <Input placeholder="Contoh: RT 05, Kelurahan Sukamaju, Bandung" value={aksiForm.location} onChange={e => setAksiForm({
                      ...aksiForm,
                      location: e.target.value
                    })} className="flex-1" data-id="element-4849" />
                          <Button type="button" variant="outline" size="icon" data-id="element-4850">
                            <MapPin className="h-4 w-4" data-id="element-4851" />
                          </Button>
                        </div>
                      </div>

                      {/* Submit */}
                      <Button type="submit" className="w-full h-12 text-lg bg-primary-600 hover:bg-primary-700 font-semibold" disabled={isSubmitting || !aksiForm.category || aksiForm.description.length < 20} data-id="element-4852">
                        {isSubmitting ? <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" data-id="element-4853" />
                            Mengirim...
                          </> : <>
                            <Send className="mr-2 h-5 w-5" data-id="element-4854" />
                            Kirim Laporan Aksi
                          </>}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </>}
          </section>}

        {/* CTA Section */}
        <section className="py-16 bg-primary-50" data-id="element-4855">
          <div className="container mx-auto px-4 md:px-6 text-center" data-id="element-4856">
            <Target className="h-12 w-12 text-primary-600 mx-auto mb-4" data-id="element-4857" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-4858">
              Ingin Berkontribusi Lebih?
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto" data-id="element-4859">
              Ikuti event nasional atau bergabung dengan komunitas untuk aksi
              yang lebih besar!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-4860">
              <Link to="/event" data-id="element-4861">
                <Button variant="outline" className="gap-2" data-id="element-4862">
                  Lihat Event
                </Button>
              </Link>
              <Link to="/komunitas" data-id="element-4863">
                <Button className="bg-primary-600 hover:bg-primary-700 gap-2" data-id="element-4864">
                  Gabung Komunitas
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-4865" />

      {/* Survey Modal */}
      <Modal isOpen={!!selectedSurvey && !isCompleted} onClose={resetSurvey} title={selectedSurvey?.title || 'Survei'} size="lg" data-id="element-4866">
        {selectedSurvey && <div className="space-y-6" data-id="element-4867">
            {/* Progress */}
            <div data-id="element-4868">
              <div className="flex justify-between text-sm text-gray-500 mb-2" data-id="element-4869">
                <span data-id="element-4870">
                  Pertanyaan {currentQuestion + 1} dari {mockQuestions.length}
                </span>
                <span data-id="element-4871">
                  {Math.round((currentQuestion + 1) / mockQuestions.length * 100)}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-4872">
                <div className="bg-primary-600 h-2 rounded-full transition-all duration-300" style={{
              width: `${(currentQuestion + 1) / mockQuestions.length * 100}%`
            }} data-id="element-4873" />
              </div>
            </div>

            {/* Question */}
            <div className="py-4" data-id="element-4874">
              <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-4875">
                {mockQuestions[currentQuestion].question}
              </h3>

              {mockQuestions[currentQuestion].type === 'rating' && <div className="flex justify-center gap-4" data-id="element-4876">
                  {mockQuestions[currentQuestion].options?.map((option, i) => <button key={i} onClick={() => handleAnswer(mockQuestions[currentQuestion].id, option as number)} className={cn('h-14 w-14 rounded-full border-2 flex items-center justify-center text-lg font-bold transition-all', answers[mockQuestions[currentQuestion].id] === option ? 'bg-primary-600 border-primary-600 text-white' : 'border-gray-300 text-gray-600 hover:border-primary-400')} data-id="element-4877">
                      {option}
                    </button>)}
                </div>}

              {mockQuestions[currentQuestion].type === 'multiple' && <div className="space-y-3" data-id="element-4878">
                  {mockQuestions[currentQuestion].options?.map((option, i) => <button key={i} onClick={() => handleAnswer(mockQuestions[currentQuestion].id, option as string)} className={cn('w-full p-4 rounded-lg border-2 text-left transition-all', answers[mockQuestions[currentQuestion].id] === option ? 'bg-primary-50 border-primary-600 text-primary-700' : 'border-gray-200 hover:border-primary-300')} data-id="element-4879">
                      {option}
                    </button>)}
                </div>}

              {mockQuestions[currentQuestion].type === 'text' && <Textarea placeholder="Ketik jawaban Anda di sini..." className="h-32" value={answers[mockQuestions[currentQuestion].id] as string || ''} onChange={e => handleAnswer(mockQuestions[currentQuestion].id, e.target.value)} data-id="element-4880" />}
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t" data-id="element-4881">
              <Button variant="outline" onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))} disabled={currentQuestion === 0} data-id="element-4882">
                Sebelumnya
              </Button>
              <Button className="bg-primary-600 hover:bg-primary-700" onClick={handleNext} disabled={!answers[mockQuestions[currentQuestion].id] || isSubmitting} data-id="element-4883">
                {isSubmitting ? <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-4884" />
                    Mengirim...
                  </> : currentQuestion === mockQuestions.length - 1 ? 'Kirim Jawaban' : 'Selanjutnya'}
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Completion Modal */}
      <Modal isOpen={isCompleted} onClose={resetSurvey} title="" size="md" data-id="element-4885">
        <div className="text-center py-6" data-id="element-4886">
          <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4887">
            <CheckCircle className="h-10 w-10 text-green-600" data-id="element-4888" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4889">
            Terima Kasih!
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-4890">
            Jawaban Anda telah berhasil dikirim. Partisipasi Anda sangat berarti
            untuk meningkatkan pengelolaan sampah di Indonesia.
          </p>

          {selectedSurvey?.reward && <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6" data-id="element-4891">
              <Gift className="h-8 w-8 text-orange-600 mx-auto mb-2" data-id="element-4892" />
              <p className="text-sm text-orange-800" data-id="element-4893">
                Anda berhak mendapatkan <strong data-id="element-4894">{selectedSurvey.reward}</strong>
                !
                <br data-id="element-4895" />
                <span className="text-xs" data-id="element-4896">
                  Akan dikirim ke email Anda dalam 1x24 jam.
                </span>
              </p>
            </div>}

          <div className="flex gap-3 justify-center" data-id="element-4897">
            <Button variant="outline" onClick={resetSurvey} data-id="element-4898">
              Kembali ke Daftar
            </Button>
            <Link to="/" data-id="element-4899">
              <Button className="bg-primary-600 hover:bg-primary-700" data-id="element-4900">
                Ke Beranda
              </Button>
            </Link>
          </div>
        </div>
      </Modal>
    </div>;
}